package com.sddamico.push

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlin.js.Promise

data class PushSubscriptionData(
    val endpoint: String,
    val p256dh: String,
    val auth: String,
)

fun isPushSupported(): Boolean {
    return js("('serviceWorker' in navigator && 'PushManager' in window)") as Boolean
}

fun getPermissionState(): String {
    return js("typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'") as String
}

suspend fun registerServiceWorker() {
    val registration = (js("navigator.serviceWorker.register('/sw.js')") as Promise<dynamic>).await()
    println("Service worker registered: $registration")
}

suspend fun subscribeToPush(vapidPublicKey: String): PushSubscriptionData? {
    try {
        // Request notification permission
        val permission = (js("Notification.requestPermission()") as Promise<dynamic>).await() as String
        if (permission != "granted") return null

        // Get service worker registration
        val registration = (js("navigator.serviceWorker.ready") as Promise<dynamic>).await()

        // Convert VAPID public key from base64url to Uint8Array
        val applicationServerKey = urlBase64ToUint8Array(vapidPublicKey)

        // Subscribe to push
        val options = js("{}")
        options.userVisibleOnly = true
        options.applicationServerKey = applicationServerKey

        val subscription = (registration.pushManager.subscribe(options) as Promise<dynamic>).await()

        val endpoint = subscription.endpoint as String
        val keys = subscription.toJSON().keys
        val p256dh = keys.p256dh as String
        val auth = keys.auth as String

        return PushSubscriptionData(endpoint, p256dh, auth)
    } catch (e: Throwable) {
        println("Push subscription failed: ${e.message}")
        return null
    }
}

suspend fun unsubscribeFromPush(): Boolean {
    return try {
        val registration = (js("navigator.serviceWorker.ready") as Promise<dynamic>).await()
        val subscription = (registration.pushManager.getSubscription() as Promise<dynamic>).await()
            ?: return false
        (subscription.unsubscribe() as Promise<dynamic>).await()
        true
    } catch (e: Throwable) {
        println("Push unsubscribe failed: ${e.message}")
        false
    }
}

suspend fun getCurrentSubscription(): PushSubscriptionData? {
    return try {
        val registration = (js("navigator.serviceWorker.ready") as Promise<dynamic>).await()
        val subscription = (registration.pushManager.getSubscription() as Promise<dynamic>).await()
            ?: return null

        val endpoint = subscription.endpoint as String
        val keys = subscription.toJSON().keys
        val p256dh = keys.p256dh as String
        val auth = keys.auth as String

        PushSubscriptionData(endpoint, p256dh, auth)
    } catch (e: Throwable) {
        null
    }
}

private fun urlBase64ToUint8Array(base64String: String): dynamic {
    val padding = "=".repeat((4 - base64String.length % 4) % 4)
    val base64 = (base64String + padding)
        .replace("-", "+")
        .replace("_", "/")
    val rawData = window.atob(base64)
    val outputArray = js("new Uint8Array(rawData.length)")
    for (i in 0 until rawData.length) {
        outputArray[i] = rawData.asDynamic().charCodeAt(i)
    }
    return outputArray
}
